import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  from,
  InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';

import { signInToAnonymous } from '~/firebase/fbAuth';
import { GetConfigUtil } from '~/shared/utils/envUtil';
import { nullToUndefined } from '~/utils/typeUtil';

const createDefaultHttpLink = () => {
  const endpoint = `${
    GetConfigUtil.getPublicStaticConfig().COCOLOCALA_BFF_SERVER_URL
  }/graphql`;
  return createHttpLink({
    uri: endpoint,
    // NOTE: (@kazutoyo) 同一オリジンのリクエストの場合でもCookieとHTTP-Authorization ヘッダを送らない。
    // ステージング環境など、Basic認証が同一オリジンにかけられているときにAuthLinkで設定しているBearerトークンが上書きされてしまうため。
    // 2021/09/15現在はCookieをBFFサーバに送ってなにかするということはしていないけど、そのようなことが必要になったときは注意する
    credentials: 'omit',
  });
};

/**
 * レスポンスのnullの値をundefinedに変換するlink
 */
const nullToUndefinedLink = new ApolloLink((operation, forward) => {
  return forward(operation).map(data => {
    return nullToUndefined(data);
  });
});

export const createApolloClient = () => {
  const cache = new InMemoryCache();
  return new ApolloClient({
    // Provide required constructor field~/firebase/fbAuth
    cache: cache,
    link: nullToUndefinedLink.concat(createDefaultHttpLink()),
    credentials: 'include',
    // NOTE: (@kazutoyo) fetchPolicy: 'no-cache' でないとnullToUndefinedLinkが正常に動かないためデフォルト指定
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
      },
      query: {
        fetchPolicy: 'no-cache',
      },
      mutate: {
        fetchPolicy: 'no-cache',
      },
    },
  });
};

/**
 * Firebase Authの認証が必要なApolloClientを作成する
 * @returns
 */
export const createApolloClientWithFirebaseAuth = () => {
  const httpLink = createDefaultHttpLink();
  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          organization: {
            read: existing => {
              console.log('existing', existing);
              if (existing) {
                return existing;
              }
              return;
            },
          },
        },
      },
    },
  });
  const authLink = setContext(async (_, { headers }) => {
    // userを取得
    const { onAuthInit } = await import('~/firebase/fbAuth');
    const user = await onAuthInit();
    // IDトークンを取得
    const idToken = await (
      user ?? (await signInToAnonymous()).user
    )?.getIdToken();

    return {
      headers: {
        ...headers,
        Authorization: idToken ? `Bearer ${idToken}` : undefined,
      },
    };
  });
  return new ApolloClient({
    // Provide required constructor fields
    cache: cache,
    link: from([authLink, nullToUndefinedLink.concat(httpLink)]),
    // NOTE: (@kazutoyo) fetchPolicy: 'no-cache' でないとnullToUndefinedLinkが正常に動かないためデフォルト指定
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
      },
      query: {
        fetchPolicy: 'no-cache',
      },
      mutate: {
        fetchPolicy: 'no-cache',
      },
    },
  });
};
